import { useAuthStore } from '../store/useAuthStore';

/**
 * Acceso a los módulos y funciones habilitados para el tenant actual.
 *
 * El backend manda el mapa ya resuelto en tenant.modules (ver
 * Tenant::effectiveModules), así que acá no se reimplementa la lógica de
 * defaults ni de banderas heredadas — una sola fuente de verdad.
 *
 * Ausencia = habilitado. Esto importa mientras haya sesiones abiertas con un
 * payload viejo (sin la clave modules): no se les esconde nada de golpe.
 *
 * OJO: esto sirve para la UI. El bloqueo real vive en el middleware
 * 'module:' del backend; esconder un botón no protege el endpoint.
 */
// Módulos que NO se asumen habilitados cuando falta el mapa (payload viejo).
// Tutor AI históricamente venía apagado y se prendía por tenant; sin esto, un
// cliente con una respuesta previa a este cambio lo mostraría sin querer.
const LEGACY_FALLBACK = {
    tutor: (tenant) => tenant?.settings?.is_tutor_enabled === true,
};

export function useModules() {
    const tenant = useAuthStore(state => state.tenant);
    const role = useAuthStore(state => state.user?.role);

    // El super admin no pertenece a ningún tenant: no se le filtra nada.
    const isSuperAdmin = role === 'super_admin';
    const modules = tenant?.modules ?? null;

    const isModuleEnabled = (moduleKey) => {
        if (isSuperAdmin) return true;
        if (!modules) {
            const fallback = LEGACY_FALLBACK[moduleKey];
            return fallback ? fallback(tenant) : true;
        }
        const entry = modules[moduleKey];
        return entry ? entry.enabled !== false : true;
    };

    const isFeatureEnabled = (moduleKey, featureKey) => {
        if (isSuperAdmin) return true;
        if (!isModuleEnabled(moduleKey)) return false;
        if (!modules) return true;
        const entry = modules[moduleKey];
        if (!entry) return true;
        return entry.features?.[featureKey] !== false;
    };

    return { modules, isModuleEnabled, isFeatureEnabled };
}
